import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";

import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Rule } from "@/components/marketing/Rule";
import { ArticleCard } from "@/components/marketing/ArticleCard";

import { buildBreadcrumbJsonLd, buildOrganizationJsonLd } from "@/lib/seo";
import { useFeaturedArticle, useMarketingArticles } from "@/hooks/useMarketingArticles";
import { RESOURCE_CATEGORIES, isResourceCategorySlug } from "@/lib/marketing-categories";
import { cn } from "@/lib/utils";
import { useLocale, pickDbText } from "@/lib/locale";

const PAGE_SIZE = 12;

export default function ResourcesHubPage() {
  const { t, i18n } = useTranslation();
  const uiLocale = useLocale();
  const [search] = useSearchParams();
  const activeCategory = search.get("category");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const { data: featured } = useFeaturedArticle();
  const { data: articles = [] } = useMarketingArticles();

  const filtered = useMemo(() => {
    let rows = articles;
    if (activeCategory && isResourceCategorySlug(activeCategory)) {
      rows = rows.filter((a) => a.category === activeCategory);
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      rows = rows.filter((a) => {
        const title = pickDbText(a, "title", uiLocale);
        const excerpt = pickDbText(a, "excerpt", uiLocale);
        return title.toLowerCase().includes(q) || excerpt.toLowerCase().includes(q);
      });
    }
    return rows.filter((a) => !featured || a.slug !== featured.slug);
  }, [articles, activeCategory, query, uiLocale, featured]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const slice = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <SeoHead
        title={t("marketing.resources.hub.seo.title")}
        description={t("marketing.resources.hub.seo.description")}
        path="/ressources"
        locale={uiLocale}
        jsonLd={[
          buildOrganizationJsonLd(),
          buildBreadcrumbJsonLd([
            { name: t("marketing.breadcrumbs.home"), path: "/" },
            { name: t("marketing.header.nav.resources"), path: "/ressources" },
          ]),
        ]}
      />

      <Section padding="lg">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.resources.hub.eyebrow")}</Eyebrow>
          <DisplayHeading level={1} className="mt-4">{t("marketing.resources.hub.title")}</DisplayHeading>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">{t("marketing.resources.hub.lead")}</p>
        </div>

        <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder={t("marketing.resources.hub.searchPlaceholder")}
              className="h-11 w-full border border-rule bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-foreground"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Link
              to="/ressources"
              className={cn(
                "inline-flex h-9 items-center border px-3 text-xs font-medium uppercase tracking-wider transition-colors",
                !activeCategory ? "border-foreground bg-foreground text-background" : "border-rule text-muted-foreground hover:border-foreground hover:text-foreground",
              )}
            >
              {t("marketing.resources.hub.all")}
            </Link>
            {RESOURCE_CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/ressources?category=${c.slug}`}
                className={cn(
                  "inline-flex h-9 items-center border px-3 text-xs font-medium uppercase tracking-wider transition-colors",
                  activeCategory === c.slug ? "border-accent-press bg-accent-press text-accent-press-foreground" : "border-rule text-muted-foreground hover:border-foreground hover:text-foreground",
                )}
              >
                {t(`marketing.resources.categories.${c.i18n}.label`)}
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {featured && !activeCategory && !query && (
        <Section padding="sm" tone="paper">
          <ArticleCard article={featured} variant="featured" />
        </Section>
      )}

      <Rule />

      <Section padding="md">
        <div className="grid grid-cols-1 gap-px bg-rule md:grid-cols-2 lg:grid-cols-3">
          {slice.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="mt-10 text-center text-sm text-muted-foreground">{t("marketing.resources.hub.empty")}</p>
        )}

        {totalPages > 1 && (
          <nav className="mt-12 flex items-center justify-center gap-2" aria-label="Pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                aria-current={p === page ? "page" : undefined}
                className={cn(
                  "h-9 min-w-[36px] border px-3 text-sm transition-colors",
                  p === page ? "border-foreground bg-foreground text-background" : "border-rule text-muted-foreground hover:border-foreground hover:text-foreground",
                )}
              >
                {p}
              </button>
            ))}
          </nav>
        )}
      </Section>
    </>
  );
}
